import { Component, Input, OnDestroy, OnInit, SimpleChanges, OnChanges } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { AutomationRulesActions } from '@app/features/automation-rules/actions';
import { AutomationRulesService } from '@app/features/automation-rules/automation-rules.service';
import { VariantMeasurementsService } from '@app/services/variants/variant-measurements.service';
import { OrderTag } from '@app/sharedModule/models/order-tag.model';
import { ProductTag } from '@app/sharedModule/models/product-tag.model';
import {
  DEFAULT_WEIGHT_UNIT,
  filterConfig,
  InputType,
  RulesConditionsOperator,
  RulesFilterEntity,
} from '@pdqdev/pdq-shared/dist/entities';
import { IBoxRules, IBoxSelectedRule } from '@pdqdev/pdq-shared/dist/interfaces';
import { Subscription } from 'rxjs';
import { catchError, debounceTime, first, switchMap, tap } from 'rxjs/operators';
import { FieldSubType, SettingsEditPackageService } from '../edit-package.service';
import { FilterEntities } from '@pages/workspace/assign/components/advanced-filters/config/models';
import { ShopService } from '@app/services/shops/shop.service';
import { DropdownOption } from '@app/storybook/components/dropdown/dropdown.component';

@Component({
  selector: 'app-settings-package-condition',
  templateUrl: './package-condition.component.html',
  styleUrls: ['./package-condition.component.scss'],
  standalone: false,
})
export class SettingsPackageConditionComponent implements OnInit, OnDestroy, OnChanges {
  get rulesArray(): UntypedFormArray {
    return this.settingsEditPackageService.formGroup.controls.rulesArray as UntypedFormArray;
  }

  @Input() rules: IBoxRules;
  conditionHeaderOptions;
  defaultCondition;
  controlsSubscriptions$ = [];
  autocompleteList = {};
  maximumAutocompleteWidth: number = 400;
  isLoadingResults;
  currentConditionsSelectValue: AutomationRulesActions.SelectedCondition[] = [null];
  fieldSubType = FieldSubType;
  inputType = InputType;
  subscription: Subscription;
  rulesFilterEntity = RulesFilterEntity;
  generatedRules: { index: number; isGeneratedByOrderInstance: boolean }[] = [];
  defaultWeightUnits = DEFAULT_WEIGHT_UNIT;
  readonly InputType = InputType;

  constructor(
    private formBuilder: UntypedFormBuilder,
    public settingsEditPackageService: SettingsEditPackageService,
    private automationRulesService: AutomationRulesService,
    private variantMeasurementsService: VariantMeasurementsService,
    private shopService: ShopService,
  ) {}

  ngOnInit(): void {
    const allCondition = Object.values(RulesConditionsOperator).map((operator) => {
      return { label: operator, value: operator };
    });
    this.conditionHeaderOptions = allCondition;
    this.defaultCondition = allCondition[allCondition.length - 1];
    this.initConditions();
    this.subscription = this.settingsEditPackageService.resetConditions.subscribe(() => {
      this.unsubscribeControlsSubscriptions();
      this.controlsSubscriptions$ = [];
      this.settingsEditPackageService.formGroup.controls.rulesArray.reset();
      this.initConditions();
    });
    this.shopService.shop$.pipe(first()).subscribe((shop) => {
      if (shop?.metadata?.weightUnits) this.defaultWeightUnits = shop.metadata.weightUnits;
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.rules && !changes.rules.firstChange) {
      // 'Rules input changed, reinitializing conditions'
      this.deleteCondition(0, 0);
      this.unsubscribeControlsSubscriptions();
      this.controlsSubscriptions$ = [];
      this.settingsEditPackageService.formGroup.controls.rulesArray.reset();
      this.initConditions();
    }
  }

  ngOnDestroy(): void {
    this.unsubscribeControlsSubscriptions();
    this.subscription.unsubscribe();
  }

  unsubscribeControlsSubscriptions() {
    this.controlsSubscriptions$.forEach((subscriptions) => {
      subscriptions.forEach((subscription) => {
        subscription?.unsubscribe();
      });
    });
  }

  initConditions() {
    if (this.rules?.conditionsSubsets?.length) {
      this.rules.conditionsSubsets.forEach((rules, index) => {
        this.generatedRules.push({
          index: index,
          isGeneratedByOrderInstance: rules.isGeneratedByOrderInstance ?? false,
        });
        this.addConditions(rules.rulesArray);
      });
      this.settingsEditPackageService.updateRulesMetadata(this.generatedRules);
    } else {
      this.addConditions();
      this.settingsEditPackageService.resetRulesMetadata();
    }
  }

  getRulesControls(conditionsIndex: number) {
    return (this.rulesArray.controls[conditionsIndex] as UntypedFormArray).controls;
  }

  addConditions(rulesData?: IBoxSelectedRule[]) {
    this.controlsSubscriptions$.push([]);

    this.rulesArray.push(this.formBuilder.array([]));
    if (rulesData?.length) {
      rulesData.forEach((ruleData) => {
        this.addCondition(this.controlsSubscriptions$.length - 1, ruleData);
      });
    } else {
      this.addCondition(this.controlsSubscriptions$.length - 1);
    }
  }

  addCondition(conditionsIndex: number, ruleData?: IBoxSelectedRule) {
    this.controlsSubscriptions$[conditionsIndex].push(null);
    const paramValidators = [];
    if (ruleData) {
      paramValidators.push(Validators.required);
      if (
        ruleData.filterEntity === RulesFilterEntity.WEIGHT ||
        ruleData.filterEntity === RulesFilterEntity.ITEM_QUANTITY
      ) {
        paramValidators.push(Validators.min(0.001));
      }
    }
    const rulesArray = this.getRulesControls(conditionsIndex);
    rulesArray.push(
      this.formBuilder.group({
        filterEntity: this.formBuilder.control(ruleData ? ruleData.filterEntity : ''),
        conditionEntity: this.formBuilder.control(ruleData ? ruleData.conditionEntity : ''),
        param1: this.formBuilder.control(ruleData ? ruleData.value[0] : '', paramValidators),
        param2: this.formBuilder.control(ruleData ? ruleData.value[1] : ''),
        stringParam: this.formBuilder.control(''),
      }),
    );
    const index = rulesArray.length - 1;
    rulesArray[index].get('param1').valueChanges.subscribe(() => {
      this.settingsEditPackageService.validateBoxRulesDataError = null;
      rulesArray[index].updateValueAndValidity();
      this.rulesArray.controls[conditionsIndex].updateValueAndValidity();
      this.rulesArray.markAsDirty();
    });
    rulesArray[index].get('param2').valueChanges.subscribe(() => {
      this.markControlAsDirty(this.rulesArray.controls[conditionsIndex] as UntypedFormArray);
      this.markControlAsDirty(this.rulesArray);
    });
    if (ruleData) {
      const conditionControl = <UntypedFormGroup>rulesArray[index];
      const params = this.settingsEditPackageService.getParamsBySelectedCondition(conditionControl);
      const stringParam = params.find((p) => p.fieldType === InputType.STRING);
      this.initAutocompleteByType(
        stringParam,
        conditionControl,
        conditionsIndex,
        index,
        ruleData.filterEntity,
        ruleData,
      );
    }
  }

  entityChanged(data, conditionControl: UntypedFormGroup, conditionsGroup: UntypedFormGroup) {
    conditionControl.controls.filterEntity.setValue(data.value);
    this.markControlAsDirty(conditionControl.controls.filterEntity);
    conditionControl.controls.conditionEntity.setValue(null);
    conditionControl.controls.conditionEntity.setValidators(Validators.required);
    conditionControl.controls.conditionEntity.updateValueAndValidity();
    conditionControl.controls.param1.setValidators(Validators.required);
    conditionControl.controls.param1.updateValueAndValidity();
    this.settingsEditPackageService.validateBoxRulesDataError = null;
    if (data.value === RulesFilterEntity.WEIGHT || data.value === RulesFilterEntity.ITEM_QUANTITY) {
      conditionControl.controls.param1.setValidators([Validators.required, Validators.min(0.001)]);
    }
    this.markControlAsDirty(conditionsGroup);
  }

  conditionChanged(
    data,
    conditionControl: UntypedFormGroup,
    conditionsIndex: number,
    conditionIndex: number,
    conditionsGroup: UntypedFormGroup,
  ) {
    conditionControl.controls.conditionEntity.setValue(data.value);
    this.markControlAsDirty(conditionControl.controls.conditionEntity);
    const entityValue = conditionControl.controls.filterEntity.value;
    const params = this.settingsEditPackageService.getParamsBySelectedCondition(conditionControl);
    params.forEach((p, index) => {
      const defaultValue = this.getDefaultValueByInputType(p.fieldType, entityValue);
      if (defaultValue !== null) {
        conditionControl.controls[`param${index + 1}`].setValue(defaultValue);
      } else {
        conditionControl.controls[`param${index + 1}`].setValue('');
      }
    });
    if (params.length === 1) {
      conditionControl.controls.param2.setValue('');
    }
    if (entityValue === RulesFilterEntity.SKU || entityValue === RulesFilterEntity.PRODUCT_TYPE) {
      conditionControl.controls.param2.setValue(null);
    }
    const stringParam = params.find((p) => p.fieldType === InputType.STRING);
    this.controlsSubscriptions$[conditionsIndex][conditionIndex]?.unsubscribe();
    this.settingsEditPackageService.validateBoxRulesDataError = null;
    this.initAutocompleteByType(stringParam, conditionControl, conditionsIndex, conditionIndex, entityValue);
    this.markControlAsDirty(conditionsGroup);
  }

  initAutocompleteByType(
    stringParam,
    conditionControl: UntypedFormGroup,
    conditionsIndex: number,
    conditionIndex: number,
    entityValue: string,
    ruleData?: IBoxSelectedRule,
  ) {
    if (stringParam) {
      if (stringParam.fieldSubType === FieldSubType.autocomplete) {
        switch (entityValue) {
          case RulesFilterEntity.TAGS: {
            this.initSearchTerm(conditionControl, conditionsIndex, conditionIndex);
            break;
          }
          case RulesFilterEntity.SKU: {
            this.loadAutocompleteByType(RulesFilterEntity.SKU);
            break;
          }
          case RulesFilterEntity.BARCODE: {
            this.loadAutocompleteByType(RulesFilterEntity.BARCODE);
            break;
          }
        }
      } else if (ruleData && stringParam.isMultiple && stringParam.fieldSubType !== FieldSubType.autocomplete) {
        conditionControl.controls.param1.setValue(ruleData.value[0][0]);
      }
    }
  }

  loadAutocompleteByType(key: RulesFilterEntity): void {
    if (!this.autocompleteList[key]) {
      this.isLoadingResults = true;
      this.variantMeasurementsService
        .getShopVariantAutocompleteByType(key)
        .pipe(
          catchError((error) => {
            this.isLoadingResults = false;
            return error;
          }),
        )
        .subscribe((data) => {
          this.autocompleteList[key] = data;
          this.isLoadingResults = false;
        });
    }
  }

  getDefaultValueByInputType(inputType: InputType, entityValue: RulesFilterEntity) {
    switch (inputType) {
      case InputType.WEIGHT_UNIT: {
        return this.defaultWeightUnits;
      }
      case InputType.INTEGER: {
        return entityValue !== RulesFilterEntity.SKU && entityValue !== RulesFilterEntity.PRODUCT_TYPE ? 0 : null;
      }
      default: {
        return null;
      }
    }
  }

  initSearchTerm(conditionControl: UntypedFormGroup, conditionsIndex, conditionIndex): void {
    const subscription = conditionControl.controls.stringParam.valueChanges
      .pipe(
        tap(() => (this.isLoadingResults = true)),
        debounceTime(500),
        switchMap((value: any) => {
          if (value?.trim().length > 0) {
            return this.automationRulesService.getAutoComplete(value?.trim(), FilterEntities.TAG);
          } else {
            this.autocompleteList[RulesFilterEntity.TAGS] = [];
            this.isLoadingResults = false;
            return [];
          }
        }),
      )
      .subscribe((res: { orderTags: OrderTag[]; productTags: ProductTag[] }) => {
        this.autocompleteList[RulesFilterEntity.TAGS] = res?.orderTags
          ?.map((tag) => tag.name)
          .concat(res.productTags.map((tag) => tag.name));
        this.isLoadingResults = false;
      });
    this.controlsSubscriptions$[conditionsIndex][conditionIndex] = subscription;
  }

  deleteCondition(conditionsIndex: number, conditionIndex: number) {
    const conditionsArray = this.rulesArray.controls[conditionsIndex] as UntypedFormArray;
    conditionsArray.removeAt(conditionIndex);
    this.controlsSubscriptions$[conditionsIndex][conditionIndex]?.unsubscribe();
    this.controlsSubscriptions$[conditionsIndex].splice(conditionIndex, 1);
    if (!conditionsArray.controls.length) {
      this.rulesArray.removeAt(conditionsIndex);
      this.controlsSubscriptions$.splice(conditionsIndex, 1);
    }
    this.markControlAsDirty(this.rulesArray);
  }

  getFilterEntityLabelByValue(filterEntity: string) {
    return filterConfig.find((x) => x.value === filterEntity)?.name;
  }

  getConditionEntityLabelByValue(conditionGroup: UntypedFormGroup) {
    return this.settingsEditPackageService.optionsByEntity[conditionGroup.controls.filterEntity.value].find(
      (x) => x.value === conditionGroup.controls.conditionEntity.value,
    )?.name;
  }

  onTagSelect(conditionsIndex: number, conditionIndex: number, tag: string) {
    const group = this.getRulesControls(conditionsIndex)[conditionIndex] as UntypedFormGroup;
    const control = group.controls.stringParam;
    this.settingsEditPackageService.validateBoxRulesDataError = null;

    // must use copy array because we get the real ref to filterValueArray from table
    control.reset('', { onlySelf: true });

    const tagsCondition = {
      ...this.currentConditionsSelectValue[conditionIndex],
    };
    const newSelectedTags: string[] = [...group.controls.param1.value];
    const optionExists = group.controls.param1.value && group.controls.param1.value.find((val) => val === tag);
    if (!optionExists) {
      newSelectedTags.push(tag);
      group.controls.param1.setValue(newSelectedTags);
      const selectedTags = [...newSelectedTags];
      tagsCondition.value = {
        selectedTags,
      };
    }
  }

  isTextOverflowed(event: HTMLElement | any) {
    const offsetWidth = event?.elementRef.nativeElement?.firstElementChild?.offsetWidth ?? 0;
    return !(offsetWidth < this.maximumAutocompleteWidth);
  }

  getControlValue(conditionsIndex: number, conditionIndex: number) {
    const group = this.getRulesControls(conditionsIndex)[conditionIndex] as UntypedFormGroup;
    return group.controls.stringParam.value;
  }

  onTagRemove(conditionsIndex: number, conditionIndex: number, tagIndex: number): void {
    // must use copy array because we get the real ref to filterValueArray from table
    const tagsCondition = {
      ...this.currentConditionsSelectValue[conditionIndex],
    };
    const group = this.getRulesControls(conditionsIndex)[conditionIndex] as UntypedFormGroup;
    const removeSelectedTags: string[] = [...group.controls.param1.value];
    removeSelectedTags.splice(tagIndex, 1);
    const selectedTags = [...removeSelectedTags];
    tagsCondition.value = {
      selectedTags,
    };
    group.controls.param1.setValue(removeSelectedTags);
    this.markControlAsDirty(group);
  }

  isNoResults(list: string[], conditionsIndex, index: number) {
    const searchTerm = this.getControlValue(conditionsIndex, index);
    return !list || !list.filter((item) => item?.toLowerCase().includes(searchTerm?.toLowerCase()) === true)?.length;
  }

  markControlAsDirty(control: AbstractControl) {
    control?.markAsDirty();
    control?.updateValueAndValidity();
    this.settingsEditPackageService.formGroup.updateValueAndValidity();
  }

  isShowInfoDivOfCondition(conditionIndex: number) {
    return this.generatedRules[conditionIndex]?.isGeneratedByOrderInstance ?? false;
  }

  findDefaultOption(controlValue, options: DropdownOption[], inputType: InputType) {
    if (!controlValue || !options?.length) {
      return null;
    }
    const currentOption = options.find((option) => {
      if (inputType == InputType.STRING) {
        return option.value == controlValue;
      } else if (inputType == InputType.JSON) {
        return option.value == controlValue.value;
      }
    });
    if (!currentOption) {
      return null;
    }
    return {
      label: currentOption.label,
      value: currentOption.value,
    };
  }
}
